import { Injectable } from "@angular/core";
import { FilteringExpressionsTree, FilteringLogic, IFilteringExpression, IFilteringExpressionsTree, IgxGridComponent, } from "@infragistics/igniteui-angular";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { Observable, Subject } from "rxjs";
import { MetadataKey } from "../../interfaces";
import { CachedCodeLookupService } from "../cachedCodeLookupService";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";

export interface LookupFilterList
{
    Key: string;
    Filter: string;
}

export enum FILTER_OPERATION
{
    CONTAINS = "contains",
    STARTS_WITH = "startswith",
    ENDS_WITH = "endswith",
    EQUALS = "eq",
    DOES_NOT_EQUAL = "ne",
    DOES_NOT_CONTAIN = "not contains",
    GREATER_THAN = "gt",
    LESS_THAN = "lt",
    LESS_THAN_EQUAL = "le",
    GREATER_THAN_EQUAL = "ge",
}

@Injectable({ providedIn: "root" })
export class GridFilterService
{
    private readonly log = getLoggerFor("GridFilterService");

    filtersToRun: (() => Promise<LookupFilterList>)[] = [];

    constructor(
        private readonly $odataFilterFactory: ODataFilterFactoryService,
        private $cachedCodeLookupService: CachedCodeLookupService,
    ) { }

    buildOdataFilter(filteringTree: IFilteringExpressionsTree, properties: PropertyMetadata[], recentIds: MetadataKey[] = null, idProperty: string = null): Promise<string>
    {
        const self = this;
        return Promise.resolve(this.buildOdataFilterImpl(filteringTree, properties, recentIds, idProperty))
            .then(filter =>
            {
                const loaders: Promise<LookupFilterList>[] = [];
                self.filtersToRun.forEach(f => loaders.push(f()));
                return Promise.all(loaders).then((results: LookupFilterList[]) =>
                {
                    self.filtersToRun = [];
                    results.forEach(r => filter = filter.replace(`{{${r.Key}}}`, r.Filter));
                    return filter;
                });
            });
    }

    buildOdataFilterImpl(filteringTree: IFilteringExpressionsTree, properties: PropertyMetadata[], recentIds: MetadataKey[], idProperty: string, initial: boolean = true): string
    {
        const self = this;
        let baseQueryString = "";
        let filter = "";

        if (recentIds && idProperty)
        {
            const isNumeric = Number.isInteger(recentIds[0]);
            recentIds = recentIds.map(id => !isNumeric ? `'${id}'` : id);
            let recentIdFilter = `(${idProperty} in(${recentIds.join(",")}))`;
            baseQueryString = this.$odataFilterFactory.createBuilder()
                .constantFilter(recentIdFilter)
                .build();
        }
        else if (filteringTree && filteringTree.filteringOperands && filteringTree.filteringOperands.length > 0)
        {
            filteringTree.filteringOperands.forEach(filterOperand =>
            {
                if (filter !== "") filter += ` ${FilteringLogic[filteringTree.operator].toLowerCase()} `;
                if (filterOperand instanceof FilteringExpressionsTree)
                {
                    filter += `(${this.buildOdataFilterImpl(filterOperand, properties, recentIds, idProperty, false)})`;
                }
                else
                {
                    const propertyMetadata = properties.filter(x => x.Lookup)?.find(x => x.Name === filterOperand.fieldName);
                    if (propertyMetadata)
                    {
                        let filterId = Utilities.generateRandomGuid();
                        filter += `{{${filterId}}}`;
                        this.filtersToRun.push(() => self.buildLookupFilter(filterId, propertyMetadata, filterOperand as IFilteringExpression));
                    }
                    else
                    {
                        filter += this.buildOdataExpression(filterOperand as IFilteringExpression);
                    }
                }
            });
        }

        if (!initial) baseQueryString += filter;
        else baseQueryString += filter !== "" ? `$filter=${filter}` : "";

        return baseQueryString;
    }

    buildLookupFilter(filterId: string, propertyMetadata: PropertyMetadata, operand: IFilteringExpression): Promise<LookupFilterList>
    {
        const filter = this.doBuildOdataExpression("Value", operand.searchVal, operand.condition.name, true);
        return this.$cachedCodeLookupService.Get(propertyMetadata.Lookup, filter).then(kvps =>
        {
            const builder = this.$odataFilterFactory
                .createBuilder()
                .in(operand.fieldName, kvps.map(kvp => kvp.Key));
            return <LookupFilterList>{ Key: filterId, Filter: builder.filterString() };
        });
    }

    @LogInvocation(LogLevel.DEBUG, true)
    buildOdataExpression(operand: IFilteringExpression): string
    {
        const value = operand.searchVal;
        if (value instanceof Set)
        {
            let expression = "";
            operand.condition.name = "equals";
            value.forEach(entry =>
            {
                if (expression !== "")
                    expression += ` ${FilteringLogic[1].toLowerCase()} `;
                expression += this.doBuildOdataExpression(operand.fieldName.toString(), entry, operand.condition.name);
            });
            return expression;
        }
        else
            return this.doBuildOdataExpression(operand.fieldName.toString(), value, operand.condition.name);
    }

    private doBuildOdataExpression(fieldName: string, value: Date | number | string, condition: string, buildFilter: boolean = false): string
    {
        const filterValue = typeof value === "number" || value instanceof Date
            ? value
            : value?.toLowerCase() ?? "";

        const builder = this.$odataFilterFactory.createBuilder();

        if (value instanceof Date && condition === "contains")
        {
            const d = new Date(filterValue);
            const startUtc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
            const endUtc   = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()+1)).toISOString();
            builder.constantFilter(`${fieldName} ge ${startUtc} and ${fieldName} lt ${endUtc}`);
        }
        else
        {
            switch (condition)
            {
                case "contains": {
                    builder.contains(`tolower(${fieldName})`, filterValue.toString());
                    break;
                }
                case "startsWith": {
                    // builder.startsWith(`tolower(${fieldName})`, filterValue.toString());
                    builder.startsWith(fieldName, filterValue.toString());
                    break;
                }
                case "endsWith": {
                    // builder.endsWith(`tolower(${fieldName})`, filterValue.toString());
                    builder.endsWith(fieldName, filterValue.toString());
                    break;
                }
                case "equals": {
                    builder.filterEQ(`tolower(${fieldName})`, filterValue);
                    break;
                }
                case "doesNotEqual": {
                    builder.filterNE(`tolower(${fieldName})`, filterValue);
                    break;
                }
                case "doesNotContain": {
                    builder.notContains(`tolower(${fieldName})`, filterValue.toString());
                    break;
                }
                case "greaterThan":
                case "after": {
                    builder.filterGT(fieldName, filterValue);
                    break;
                }
                case "greaterThanOrEqualTo": {
                    builder.filterGE(fieldName, filterValue);
                    break;
                }
                case "lessThan":
                case "before": {
                    builder.filterLT(fieldName, filterValue);
                    break;
                }
                case "lessThanOrEqualTo": {
                    builder.filterLE(fieldName, filterValue);
                    break;
                }
                case "empty": {
                    builder.filterEQ(`length(${fieldName})`, 0);
                    break;
                }
                case "notEmpty": {
                    builder.filterGT(`length(${fieldName})`, 0);
                    break;
                }
                case "null": {
                    builder.filterEQ(fieldName, "null");
                    break;
                }
                case "notNull": {
                    builder.filterNE(fieldName, "null");
                    break;
                }
            }
        }
        if (buildFilter)
            return builder.build();
        return builder.filterString();
    }

    filterLocalTable(tableData: any[], grid: IgxGridComponent): any[]
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(grid.filteringExpressionsTree?.filteringOperands))
            return tableData;
        if (grid?.filterStrategy)
            return grid.filterStrategy.filter(tableData, grid.filteringExpressionsTree);
        const dataCopy = Utilities.deepCopy(tableData);
        return dataCopy;
    }

    private clearFilter$ = new Subject<void>();
    get clearFilterObservable(): Observable<void>
    {
        return this.clearFilter$.asObservable();
    }

    clearCustomFilter()
    {
        this.clearFilter$.next();
    }
}
